import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { TagsUtils, TagsV2 } from '@frontend/api-tag';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, Chip, useAlert } from '@frontend/design-system';
import { MessageInsightsTagSelector } from './message-insights-tag-selector';

type Props = {
  sms: SMS | null;
  locationId: string;
};

export const MessageInsightsTagsSection = ({ sms, locationId }: Props) => {
  const { t } = useTranslation('message-insights-panel');
  const alert = useAlert();

  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(sms?.tags ?? []);

  const { data: allTagsData } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      enabled: !!selectedTagIds.length,
    },
  });

  const tagMap = useMemo(() => {
    return (
      allTagsData?.tags.reduce((acc, tag) => {
        acc[tag.id] = tag;
        return acc;
      }, {} as Record<string, (typeof allTagsData.tags)[number]>) ?? {}
    );
  }, [allTagsData]);

  const displayedTags = selectedTagIds.map((tagId) => tagMap[tagId]).filter(Boolean);

  const { mutate: applyTag } = SMSDataV4.Mutations.useApplyTagMutation({
    options: {
      onSuccess: (_, request) => {
        setSelectedTagIds((prev) => Array.from(new Set([...prev, request.tagId])));
        alert.success(t('Tag added successfully.'));
      },
      onError: () => {
        alert.error(t('Failed to add tag.'));
      },
    },
  });

  const { mutate: dismissTag } = SMSDataV4.Mutations.useDismissTagMutation({
    options: {
      onSuccess: (_, request) => {
        setSelectedTagIds((prev) => prev.filter((id) => id !== request.tagId));
        alert.success(t('Successfully removed tag.'));
      },
      onError: () => {
        alert.error(t('Failed to remove tag.'));
      },
    },
  });

  useEffect(() => {
    setSelectedTagIds(sms?.tags ?? []);
  }, [sms?.tags]);

  if (!sms) return null;

  const handleApplyTag = (tag: Tag) => {
    applyTag({
      id: sms.id,
      locationId,
      threadId: sms.threadId,
      tagId: tag.id,
      createdAt: sms.createdAt,
    });
  };

  const handleRemoveTag = (tag: Tag) => {
    dismissTag({
      id: sms.id,
      locationId,
      threadId: sms.threadId,
      tagId: tag.id,
    });
  };

  return (
    <div>
      {displayedTags.length === 0 && isEditing && (
        <div style={{ padding: theme.spacing(1) }}>
          <MessageInsightsTagSelector
            onClose={() => setIsEditing(false)}
            onApplyTag={handleApplyTag}
            existingTagIds={displayedTags.map((tag) => tag.id)}
          />
        </div>
      )}
      {displayedTags.length > 0 ? (
        <div css={styles.tagsRow}>
          {displayedTags.map((tag) => (
            <Chip.Tag
              key={tag.id}
              isRemovable
              onRemoveClick={() => handleRemoveTag(tag)}
              color={TagsUtils.convertStringToTagColor(tag.color)}
            >
              {tag.name}
            </Chip.Tag>
          ))}
          {isEditing && (
            <MessageInsightsTagSelector
              onClose={() => setIsEditing(false)}
              existingTagIds={displayedTags.map((tag) => tag.id)}
              onApplyTag={handleApplyTag}
            />
          )}
          <Button
            iconName='label'
            css={styles.addTagBtn}
            variant='secondary'
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          />
        </div>
      ) : (
        <div css={styles.addTagBtnWrapper}>
          <Button
            iconName='label'
            css={styles.addTagBtn}
            variant='secondary'
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          />
        </div>
      )}
    </div>
  );
};

const styles = {
  addTagBtnWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  addTagBtn: css`
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    svg {
      color: ${theme.colors.neutral90};
    }
  `,
  tagsRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
  `,
};
